import React from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { IconButton } from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import { TimePicker } from '@atlaskit/datetime-picker';
import Form, { CheckboxField, ErrorMessage, Field } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import EditorInfoIcon from '@atlaskit/icon/core/migration/information--editor-info';
import Lozenge from '@atlaskit/lozenge';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Text } from '@atlaskit/primitives/compiled';
import { RadioGroup } from '@atlaskit/radio';
import SectionMessage from '@atlaskit/section-message';
import Select, { type ValueType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import Tooltip from '@atlaskit/tooltip';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import {
	ConditionFilter,
	type IFieldOptions,
	type IFilterValues,
} from '@atlassian/eoc-condition-filter';
import { DayTimeInterval, type DayTimeRangePickerValue } from '@atlassian/eoc-day-time-interval';
import { Flex } from '@atlassian/eoc-flex';

import { SPOTLIGHT_NAMES } from '../../../common/constants';
import commonMessages from '../../../common/messages';
import { type RouteOptionType, type RoutingRuleFormValues } from '../../../common/types';
import { FieldResetter, OptionLabel } from '../../../common/ui/styled';
import { getDefaultTimes, getMatchType, getOperationName } from '../../../common/utils';
import { useRoutingRuleModalController } from '../../../controllers/routing-rule-modal';

import messages from './messages';
import {
	ConditionFilterWrapper,
	DayTimeIntervalWrapper,
	RadioWrapper,
	SectionMessageWrapper,
	SelectWrapper,
	TimePickerWrapper,
	TimezoneWrapper,
} from './styled';
import { type RoutingRuleModalProps } from './types';

export const RoutingRuleModal = ({
	isOpen,
	isCloning,
	isLoading,
	onClose,
	onSubmit,
	onOpen,
	conditionFields,
	routeToOptions,
	data,
	defaultTimeZone,
	returnFocusRef,
}: RoutingRuleModalProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { timezones, isTimezonesLoading } = useRoutingRuleModalController();
	const { formatMessage } = useIntl();

	const handleClose = () => {
		if (onClose) {
			onClose();
		}
	};

	const isTimeRestricted = data && data.timeRestrictedInterval.timeRestrictions.length > 0;

	const isDeleted = data && !!data.deleted;

	const handleDefaultEntity = () => {
		if (routeToOptions) {
			const options = routeToOptions.flatMap((option) => option.options);

			if (data && data.deleted) {
				return null;
			}

			const defaultValue = options.find((option) => {
				if (data && option?.value === data.entityId) {
					return option?.value === data.entityId;
				}
			});

			const noOneValue = options.find((option) => option?.value === 'noOne');

			return defaultValue || noOneValue;
		}
	};

	const radioGroupOptions = [
		{
			name: 'everyday',
			value: 'everyday',
			label: formatMessage(messages.forEveryday),
		},
		{
			name: 'customize-days',
			value: 'customize-days',
			label: formatMessage(messages.customizeDays),
		},
	];

	const getDefaultTimeRestrictedInterval = () => {
		if (isTimeRestricted) {
			if (data.timeRestrictedInterval.timeRestrictions[0].startDay) {
				return 'customize-days';
			}

			return 'everyday';
		}

		return 'everyday';
	};

	const getDefaultStartTime = () => {
		if (isTimeRestricted) {
			return `${data?.timeRestrictedInterval.timeRestrictions[0].startHour || '00'}:${
				data?.timeRestrictedInterval.timeRestrictions[0].startMinute || '00'
			}`;
		} else {
			return `00:00`;
		}
	};

	const getDefaultEndTime = () => {
		if (isTimeRestricted) {
			return `${data?.timeRestrictedInterval.timeRestrictions[0].endHour || '00'}:${
				data?.timeRestrictedInterval.timeRestrictions[0].endMinute || '00'
			}`;
		} else {
			return '06:00';
		}
	};

	const getDefaultCustomTimeRestrictionValue = () => {
		if (isTimeRestricted) {
			return data.timeRestrictedInterval.timeRestrictions as DayTimeRangePickerValue[];
		} else {
			return [
				{
					startDay: 2,
					startHour: 0,
					startMinute: 0,
					endDay: 3,
					endHour: 6,
					endMinute: 0,
				},
			] as DayTimeRangePickerValue[];
		}
	};

	const getDefaultTimezone = () => {
		if (data && data.timeZoneId) {
			const timezoneOption = timezones?.find((timezone) => timezone.value === data.timeZoneId);

			return timezoneOption;
		}

		const defaultTimezoneOption = timezones?.find(
			(timezone) => timezone.value === defaultTimeZone.id,
		);

		return {
			label: defaultTimezoneOption?.label,
			value: '',
		};
	};

	const getModalTitle = () => {
		if (isCloning) {
			return formatMessage(messages.cloneTitle);
		}

		if (data) {
			return formatMessage(messages.editTitle);
		}

		return formatMessage(messages.addTitle);
	};

	const getDefaultName = () => {
		if (!isCloning && data) {
			return data.name;
		}

		return '';
	};

	const getDefaultConditions: () => IFilterValues = () => {
		if (data) {
			return {
				conditions: data.conditions.map((condition) => ({
					...condition,
					operation: getOperationName(condition.operation),
				})),
				matchType: getMatchType(data.conditionMatchType),
			};
		}

		return {
			conditions: [],
			matchType: 'match-all',
		};
	};

	return (
		<ModalTransition>
			{isOpen && (
				<Modal
					onOpenComplete={onOpen}
					width="large"
					onClose={(_, event) => {
						fireUIAnalytics(event, 'routingRuleModal');
						handleClose();
					}}
					shouldReturnFocus={returnFocusRef}
				>
					<Form<RoutingRuleFormValues> onSubmit={onSubmit}>
						{({ formProps }) => (
							<form id="routing-rule-modal" {...formProps}>
								<ModalHeader>
									<ModalTitle>{getModalTitle()}</ModalTitle>
								</ModalHeader>
								<ModalBody>
									<Flex flexDirection="column" rowGap={2.5}>
										{isDeleted && (
											<SectionMessage appearance="warning">
												{formatMessage(messages.routeDeletedWarning)}
											</SectionMessage>
										)}
										<FieldResetter>
											<Field
												label={formatMessage(messages.ruleTitle)}
												name="name"
												defaultValue={getDefaultName()}
											>
												{({ fieldProps }) => (
													<Textfield
														placeholder={formatMessage(messages.ruleTitlePlaceholder)}
														autoComplete="off"
														{...fieldProps}
													/>
												)}
											</Field>
										</FieldResetter>
										<SpotlightTarget name={SPOTLIGHT_NAMES.alertFilters}>
											<div>
												<Heading size="small">
													{formatMessage(messages.followingConditions)}
												</Heading>
												<ConditionFilterWrapper>
													<Field<IFilterValues>
														label={formatMessage(messages.notifyInput)}
														name="conditions"
														defaultValue={getDefaultConditions()}
														validate={(value) => {
															if (value && value.conditions.length > 0) {
																const { conditions } = value;
																const freeformFields = conditions.filter(
																	(condition) => condition.fieldFreeForm,
																);

																if (freeformFields.length > 0) {
																	const isEmpty = freeformFields.some(
																		(freeformField) => !freeformField.field,
																	);

																	if (isEmpty) {
																		return 'REQUIRED';
																	}
																}
															}
														}}
													>
														{({ fieldProps, error }) => (
															<>
																<ConditionFilter
																	fields={conditionFields.map(
																		(field) =>
																			({
																				label: field.label,
																				type: field.type,
																				value: field.id,
																			}) as IFieldOptions,
																	)}
																	{...fieldProps}
																/>
																{error === 'REQUIRED' && (
																	<ErrorMessage>
																		{formatMessage(messages.conditionRequired)}
																	</ErrorMessage>
																)}
															</>
														)}
													</Field>
												</ConditionFilterWrapper>
											</div>
										</SpotlightTarget>

										<FieldResetter>
											<SpotlightTarget name={SPOTLIGHT_NAMES.timeSlots}>
												<CheckboxField
													label={formatMessage(messages.restrictToTimeInterval)}
													name="timeRestrictedIntervalEnabled"
													defaultIsChecked={data ? data.timeRestrictedInterval.enabled : false}
												>
													{({ fieldProps: { id, ...fieldProps } }) => (
														<>
															<Checkbox
																label={formatMessage(messages.specificInterval)}
																{...fieldProps}
															/>
															{fieldProps.isChecked && (
																<Field
																	name="timeRestrictedInterval"
																	defaultValue={getDefaultTimeRestrictedInterval()}
																>
																	{({ fieldProps }) => (
																		<RadioWrapper>
																			<RadioGroup options={radioGroupOptions} {...fieldProps} />
																			{fieldProps.value === 'everyday' && (
																				<Flex columnGap={1}>
																					<TimePickerWrapper>
																						<Field
																							name="start"
																							label={formatMessage(messages.from)}
																							defaultValue={getDefaultStartTime()}
																							validate={(value) => {
																								if (!value) {
																									return formatMessage(messages.fieldRequired);
																								}
																							}}
																						>
																							{({ fieldProps: { id, ...fieldProps }, error }) => (
																								<>
																									<TimePicker
																										times={getDefaultTimes()}
																										timeIsEditable
																										{...fieldProps}
																										id={id}
																									/>
																									{error && <ErrorMessage>{error}</ErrorMessage>}
																								</>
																							)}
																						</Field>
																					</TimePickerWrapper>
																					<TimePickerWrapper>
																						<Field
																							name="end"
																							label={formatMessage(messages.to)}
																							defaultValue={getDefaultEndTime()}
																							validate={(value) => {
																								if (!value) {
																									return formatMessage(messages.fieldRequired);
																								}
																							}}
																						>
																							{({ fieldProps: { id, ...fieldProps }, error }) => (
																								<>
																									<TimePicker
																										times={getDefaultTimes()}
																										placeholder="6:00 PM"
																										timeIsEditable
																										{...fieldProps}
																										id={id}
																									/>
																									{error && <ErrorMessage>{error}</ErrorMessage>}
																								</>
																							)}
																						</Field>
																					</TimePickerWrapper>
																				</Flex>
																			)}
																			{fieldProps.value === 'customize-days' && (
																				<Field
																					name="timeRestrictions"
																					validate={(value) => {
																						if (!value) {
																							return formatMessage(messages.fieldRequired);
																						}

																						const nullExist = value.find((interval) =>
																							Object.values(interval).includes(null),
																						);

																						if (nullExist) {
																							return formatMessage(messages.fieldRequired);
																						}
																					}}
																					defaultValue={getDefaultCustomTimeRestrictionValue()}
																				>
																					{({ fieldProps, error }) => (
																						<DayTimeIntervalWrapper flexDirection="column">
																							<DayTimeInterval
																								labels={{
																									start: formatMessage(messages.from),
																									end: formatMessage(messages.to),
																								}}
																								timeIsEditable
																								autoAddNextDay
																								{...fieldProps}
																							/>
																							{error && <ErrorMessage>{error}</ErrorMessage>}
																						</DayTimeIntervalWrapper>
																					)}
																				</Field>
																			)}
																			<TimezoneWrapper>
																				<Field<ValueType<any, false>>
																					name="timeZoneId"
																					label={formatMessage(messages.timezone)}
																					defaultValue={getDefaultTimezone()}
																				>
																					{({ fieldProps: { id, ...fieldProps } }) => (
																						<Select
																							inputId={id}
																							menuPosition="fixed"
																							options={timezones}
																							isLoading={isTimezonesLoading}
																							{...fieldProps}
																							onChange={(value) => {
																								fieldProps.onChange(value);
																								const event = createAnalyticsEvent({});

																								fireUIAnalytics(
																									event,
																									'routingRule changedTimezone',
																									id,
																								);
																							}}
																						/>
																					)}
																				</Field>
																			</TimezoneWrapper>
																		</RadioWrapper>
																	)}
																</Field>
															)}
														</>
													)}
												</CheckboxField>
											</SpotlightTarget>
										</FieldResetter>
										<div>
											<Flex columnGap={0.5} alignItems="center">
												<Heading size="small">{formatMessage(messages.thenRouteUsing)}</Heading>
												<Tooltip content={formatMessage(messages.routeTargetTooltipContent)}>
													{(tooltipProps) => (
														<IconButton
															appearance="subtle"
															spacing="compact"
															label={formatMessage(messages.routeTargetTooltipLabel)}
															icon={EditorInfoIcon}
															{...tooltipProps}
														/>
													)}
												</Tooltip>
											</Flex>
											<Field<ValueType<RouteOptionType, false>>
												name="entity"
												label={formatMessage(messages.routeTo)}
												defaultValue={handleDefaultEntity()}
												validate={(value) => {
													if (!value) {
														return formatMessage(messages.routeToRequired);
													}
												}}
											>
												{({ fieldProps: { id, ...fieldProps }, error }) => (
													<div>
														<SelectWrapper>
															<Select
																placeholder={
																	isDeleted ? formatMessage(messages.deletedTeam) : undefined
																}
																options={routeToOptions}
																inputId={id}
																menuPosition="fixed"
																formatOptionLabel={(option) => (
																	<Flex
																		justifyContent="space-between"
																		alignItems="center"
																		columnGap={1}
																	>
																		<OptionLabel>{option?.label}</OptionLabel>
																		<Flex columnGap={1}>
																			{option?.enabled === false && (
																				<Lozenge appearance="moved">
																					{formatMessage(commonMessages.off)}
																				</Lozenge>
																			)}
																		</Flex>
																	</Flex>
																)}
																{...fieldProps}
															/>
															{error && <ErrorMessage>{error}</ErrorMessage>}
														</SelectWrapper>
														{fieldProps.value?.type === 'sch' && (
															<SectionMessageWrapper>
																<SectionMessage>
																	<Text as="p">{formatMessage(messages.scheduleWarning)}</Text>
																</SectionMessage>
															</SectionMessageWrapper>
														)}
													</div>
												)}
											</Field>
										</div>
									</Flex>
								</ModalBody>
								<ModalFooter>
									<Button
										appearance="subtle"
										onClick={(e, event) => {
											fireUIAnalytics(event, 'cancelButton');
											handleClose();
										}}
									>
										{formatMessage(messages.cancel)}
									</Button>
									<Button isLoading={isLoading} type="submit" appearance="primary">
										{isCloning
											? formatMessage(messages.clone)
											: formatMessage(data ? messages.update : messages.add)}
									</Button>
								</ModalFooter>
							</form>
						)}
					</Form>
				</Modal>
			)}
		</ModalTransition>
	);
};
